
import { adjustInStockMovement } from "@/api/stock-movement-api";
import { AdjustStockDialogProps } from "@/components/stock/type";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  quantity: z.coerce.number().int().min(1, "Quantity must be at least 1"),
});

export function AdjustInStockDialog({
  isOpen,
  onClose,
  onSuccess,
  stock,
  sku,
}: AdjustStockDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      quantity: 1,
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        quantity: 1,
      });
      setIsInputFocused(false);
    }
  }, [isOpen, form]);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      await adjustInStockMovement({
        stockId: stock.id.toString(),
        quantity: values.quantity,
      });
      toast({
        title: "Success",
        description: "Stock adjusted in successfully",
      });
      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to adjust in stock",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`w-[calc(100vw-2rem)] max-w-[425px] p-4 sm:p-6 ${isInputFocused
            ? 'top-[20%] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]'
            : ''
          }`}
      >
        <DialogHeader className="px-0">
          <DialogTitle className="text-lg sm:text-xl">Adjust In Stock</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm sm:text-base space-y-2">
              <div>
                Add stock for <span className="font-bold bg-yellow-100 text-yellow-900 px-2 py-0.5 rounded">{sku}</span>. This will increase your inventory.
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-muted-foreground">Current Stock:</span>
                <span className="font-semibold bg-blue-100 text-blue-900 px-3 py-1 rounded-md text-base">
                  {stock.stockInHand} {stock.stockInHand === 1 ? 'unit' : 'units'}
                </span>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4 sm:space-y-6"
          >
            <FormField
              control={form.control}
              name="quantity"
              render={({ field: { ref: fieldRef, onBlur: fieldOnBlur, ...field } }) => (
                <FormItem>
                  <FormLabel className="text-sm sm:text-base">Quantity</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      disabled={isSubmitting}
                      className="h-10 sm:h-11 text-base"
                      {...field}
                      ref={fieldRef}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={(e) => {
                        fieldOnBlur();
                        setIsInputFocused(false);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 px-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="w-full sm:w-auto h-10 sm:h-9"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto h-10 sm:h-9"
              >
                {isSubmitting ? "Adjusting..." : "Adjust In"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
